import { resolveLocale } from '../utils/locale.js';
import cookieApi from '../utils/cookie.js';
import { readConsentState } from '../utils/consent.js';
import { apiUrl } from './config.js';

const CONTACT_ENDPOINT = apiUrl('/public/v1/contact');

const getStringValue = (formData, fieldName) => {
  const value = formData.get(fieldName);
  return typeof value === 'string' ? value.trim() : '';
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        resolve('');
        return;
      }
      const [, base64 = ''] = result.split(',', 2);
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });

const buildAttachmentPayload = async (formData) => {
  const fileValue = formData.get('attachment');
  if (typeof File === 'undefined' || !(fileValue instanceof File) || fileValue.size === 0) {
    return null;
  }

  return {
    fileName: fileValue.name,
    mimeType: fileValue.type || 'application/octet-stream',
    size: fileValue.size,
    contentBase64: await readFileAsBase64(fileValue),
  };
};

export const buildContactPayload = async (form) => {
  const formData = new FormData(form);
  const countryCode = getStringValue(formData, 'countryCode');
  const phoneNumber = getStringValue(formData, 'phone');
  const locale = resolveLocale();

  return {
    fullName: getStringValue(formData, 'name'),
    email: getStringValue(formData, 'email'),
    phone: {
      countryCode,
      number: phoneNumber,
      full: [countryCode, phoneNumber].filter(Boolean).join(' ').trim(),
    },
    message: getStringValue(formData, 'message'),
    privacyAccepted: formData.get('privacy') === 'on',
    attachment: await buildAttachmentPayload(formData),
    tracking: {
      userId: cookieApi.getUserId(),
      sessionId: cookieApi.getSessionId(),
      consent: readConsentState(),
    },
    context: {
      source: 'catch-landing',
      locale,
      path: typeof location !== 'undefined' ? location.pathname : '',
      url: typeof location !== 'undefined' ? location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      submittedAt: new Date().toISOString(),
    },
  };
};

export const submitContactForm = async (form, endpoint) => {
  const targetEndpoint = endpoint || form.getAttribute('action') || CONTACT_ENDPOINT;
  const payload = await buildContactPayload(form);
  const response = await fetch(targetEndpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Contact request failed: ${response.status} ${response.statusText}`);
  }

  return response;
};
